import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import _ from 'lodash';
import { push } from 'connected-react-router';

import { PageHeader } from '../../components/PageHeader';
import Strings from '../../Strings';
import { actions } from './redux/actions';
import Table3, { columnHelper } from '../../containers/Table/Table3';
import { TextInput } from '../../components/PageHeader/TextInput';
import {
  downloadFileOnly,
  signalRender,
  convertUnixEpochToHumanReadable,
  ucFirst,
  cityDisplay,
} from '../../utils';
import Select from '../../components/Select';
import {
  isCapManager,
  isOrganizationManager,
  isShipper,
  isSuperUser,
  isOmron,
  isESIOrgName,
  isDoctor,
} from '../../utils/userRoles';
import { Button, HEADER_BUTTON_DARK_BLUE } from '../../components/PageHeader/Button';
import { openModalAction } from '../../actions/modal';
import { ActionWidget, ActionButton, ActionDropdownOnOffMenu, ItemSelectorHelper } from '../../components/ActionWidget';
import {
  DOWNLOAD_REPORT_DATA_ERROR,
  FEATURE_CONFIGURATION_RESULT,
  SHIPPER_CAPS_RESULT,
} from './redux/constants';
import { notificationActions } from '../../components/Notification/redux/actions';
import AdvancedFilters, { Filter } from '../../components/AdvancedFilters/AdvancedFilters';
import AdvancedFiltersBar from '../../components/AdvancedFilters/AdvancedFiltersBar';
import { getFiltersForRequest, urlInjected } from '../../components/AdvancedFilters/helpers';
import { getFiltersDefinition } from '../../components/AdvancedFilters/FiltersDefinition';
import { DateFilters } from '../../utils/DateFilters';
import { DeviceTypes } from '../../utils/deviceTypes';
import OrgSelect, { NO_ORG } from '../../components/OrgSelect/OrgSelect';
import { useGlobalPageSize } from '../../utils/tableUtils';
import { BatteryWithChargingStatus } from '../../components/ChargingStatus/ChargingStatus';

function LTE(props) {
  const [caps, setCaps] = React.useState([]);
  const [pagination, setPagination] = React.useState({});
  const [isLoading, setIsLoading] = React.useState(true);
  const [openActionWidget, setOpenActionWidget] = React.useState(false);
  const [multiselectedItems, setMultiselectedItems] = React.useState(false);
  const [keyForItemCheckboxes, setKeyForItemCheckboxes] = React.useState(false);

  const controllerRef = React.useRef(undefined);
  const loadingRef = React.useRef(false);
  const refreshEnabled = React.useRef(true);
  const itemSelectorHelper = React.useRef(new ItemSelectorHelper()).current;

  const globalPageSize = useGlobalPageSize();
  const request = React.useRef({
    limit: globalPageSize,
    offset: 0,
    search: '',
    filters: [],
    deviceType: props.type,
    orgId: props.orgIds.filter(id => id !== NO_ORG).length ? props.orgIds.filter(id => id !== NO_ORG) : undefined,
    status: props.orgIds.includes(NO_ORG) ? 'unassigned' : undefined,
  });

  const resetActionWidget = React.useCallback(() => {
    setKeyForItemCheckboxes(prev => !prev);
    setOpenActionWidget(false);
    itemSelectorHelper.clearItems();
  }, [itemSelectorHelper]);

  const load = React.useCallback(() => {
    if (loadingRef.current) {
      return;
    }
    loadingRef.current = true;
    const controller = new AbortController();
    controllerRef.current = controller;
    setIsLoading(true);
    props.getCaps(request.current, controller.signal).then(resp => {
      loadingRef.current = false;
      if (resp?.type === SHIPPER_CAPS_RESULT) {
        setCaps(resp.response?.data);
        setPagination(resp.response?.pagination);
        resetActionWidget();
      }
      setIsLoading(false);
    });
    resetActionWidget();
  }, [props.getCaps, resetActionWidget]);

  const prevOrgIdRef = React.useRef(props.orgIds);
  const prevFiltersRef = React.useRef(props.filters);
  const isInitialMount = React.useRef(true);

  React.useEffect(() => {
    // Initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      if (!urlInjected(window.location.search)) {
        load();
      }
      return () => {
        if (controllerRef.current) {
          props.cancelGetCaps(controllerRef.current);
        }
      };
    }

    // orgId changed
    if (props.orgIds !== prevOrgIdRef.current) {
      prevOrgIdRef.current = props.orgIds;
      request.current.orgId = props.orgIds.filter(id => id !== NO_ORG).length ? props.orgIds.filter(id => id !== NO_ORG) : undefined;
      request.current.offset = 0;
      if (props.orgIds.includes(NO_ORG)) {
        delete request.current.orgId;
        request.current.status = 'unassigned';
      } else {
        delete request.current.status;
      }
      load();
      return;
    }

    // filters changed
    if (props.filters !== prevFiltersRef.current) {
      prevFiltersRef.current = props.filters;
      onFiltersChange(props.filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.orgIds, props.filters]);

  const onRefresh = React.useCallback(() => {
    if (refreshEnabled.current) {
      refreshEnabled.current = false;
      setTimeout(() => {
        refreshEnabled.current = true;
      }, 1000);
      request.current.offset = 0;
      load();
    }
  }, [load]);

  const filtersDebounced = React.useMemo(() => _.debounce(() => {
    const newFiltersForRequest = getFiltersForRequest(props.filters);
    if (JSON.stringify(newFiltersForRequest) !== JSON.stringify(request.current.filters)) {
      request.current.filters = newFiltersForRequest;
      request.current.offset = 0;
    }
    load();
  }, 1000), [props.filters, load]);

  const onFiltersChange = React.useCallback((filters) => {
    return filtersDebounced(filters);
  }, [filtersDebounced]);

  const onDateRangeChanged = React.useCallback((option) => {
    if (option) {
      request.current.lastUpdatedStart = option.dates.startDate();
      request.current.lastUpdatedEnd = option.dates.endDate();
    } else {
      request.current.lastUpdatedStart = '';
      request.current.lastUpdatedEnd = '';
    }
    request.current.offset = 0;
    load();
  }, [load]);

  const onSleepModeChange = React.useCallback((state) => {
    let message = 'Sleep mode disabled';
    if (state === 1) {
      message = 'Sleep mode enabled';
    }

    const req = {
      devices: itemSelectorHelper.getItems(),
      features: [
        {
          name: 'sleep',
          int_value: state,
        },
      ],
    };
    props.showNotification('Processing, please wait...');
    props.featureConfiguration(req).then(resp => {
      if (resp.type === FEATURE_CONFIGURATION_RESULT) {
        props.showNotification(message);
        load();
      } else {
        props.showNotification('Error');
      }
    });
  }, [itemSelectorHelper, props.showNotification, props.featureConfiguration, load]);

  const itemChecked = React.useCallback(({ target }) => {
    if (target.checked === true) {
      itemSelectorHelper.addItem(target.id);
    } else {
      itemSelectorHelper.removeItem(target.id);
    }

    if (itemSelectorHelper.getItems().length !== 0) {
      setOpenActionWidget(true);
    } else {
      setOpenActionWidget(false);
    }

    setMultiselectedItems(itemSelectorHelper.isMultiselect());
  }, [itemSelectorHelper]);

  const onSortClick = React.useCallback((sorting) => {
    if (sorting && sorting.length > 0) {
      request.current.offset = 0;
      request.current.sortColumn = sorting[0].id;
      request.current.sortType = sorting[0].desc ? 'descending' : 'ascending';
      load();
    }
  }, [load]);

  const onRowSelection = React.useCallback((row) => {
    props.navigate(`/devices/${props.type}/${row.cap_id}/events`);
  }, [props.navigate, props.type]);

  const onOffsetChange = React.useCallback((offset) => {
    request.current.offset = offset;
    load();
  }, [load]);

  const onPageSizeChange = React.useCallback((newSize) => {
    request.current.offset = 0;
    request.current.limit = newSize;
    load();
  }, [load]);

  const onSearchQueryChangeDebounced = React.useMemo(() => _.debounce(query => {
    request.current.offset = 0;
    request.current.search = query;
    load();
  }, 1000), [load]);

  const onTextInputChange = React.useCallback((e) => {
    onSearchQueryChangeDebounced(e.target.value);
  }, [onSearchQueryChangeDebounced]);

  const onDownload = React.useCallback(() => {
    const downloadHandler = params => {
      const paramsWithType = params;
      paramsWithType.deviceType = props.type;

      switch (params.selectedOption) {
        case 'events':
          delete paramsWithType.selectedOption;
          props.downloadEvents(params).then(onDownloadBtnClickSuccessHandler);
          break;
        case 'details':
          delete paramsWithType.selectedOption;
          paramsWithType.limit = 100000;
          paramsWithType.offset = 0;
          props.downloadDetails(params).then(onDownloadBtnClickSuccessHandler);
          break;
        case 'vitals':
          delete paramsWithType.selectedOption;
          paramsWithType.simplified = true;
          props.downloadEvents(params).then(onDownloadBtnClickSuccessHandler);
          break;
        default:
          props.downloadEvents(params).then(onDownloadBtnClickSuccessHandler);
      }
      props.showNotification('Download request sent, please wait...');
    };
    const downloadRequest = { ...request.current };
    delete downloadRequest.offset;
    delete downloadRequest.limit;
    if (!downloadRequest.lastUpdatedStart) {
      downloadRequest.lastUpdatedStart = DateFilters.AllTime.dates.startDate();
    }
    if (!downloadRequest.lastUpdatedEnd) {
      downloadRequest.lastUpdatedEnd = DateFilters.AllTime.dates.endDate();
    }

    let orgNames = '';
    if (downloadRequest.orgId) {
      orgNames = props.orgs?.find(o => o.masked_id === downloadRequest.orgId)?.name;
      if (downloadRequest.orgId === NO_ORG) {
        orgNames = Strings.patientDashboard.noOrg;
      }
    }
    props.openDateRangeSelectionModal(downloadHandler, downloadRequest, orgNames);
  }, [props.type, props.downloadEvents, props.downloadDetails, props.showNotification, props.openDateRangeSelectionModal, props.orgs]);

  const onDownloadBtnClickSuccessHandler = React.useCallback((req) => {
    if (req?.type === DOWNLOAD_REPORT_DATA_ERROR) {
      props.showNotification(req?.error);
    } else {
      const receivedName = req.headers['content-disposition'].split('filename=')[1];
      const fileName = `${moment().toString() + receivedName}`;
      downloadFileOnly(req, fileName);
    }
  }, [props.showNotification]);

  const onCapsDelete = React.useCallback((event, cap_id) => {
    event.preventDefault();
    event.stopPropagation();

    const selected_device = caps.filter(device => device.cap_id === cap_id)?.pop();

    const req = {
      organization_id: selected_device?.organization?.masked_id,
      device_id: cap_id,
    };
    if (isCapManager() || isOrganizationManager() || isShipper() || isSuperUser()) {
      const data = {
        title: (
          <span>
            {Strings.deleteCapWarning} <b>{cap_id}</b>?
          </span>
        ),
        onConfirmAction: actions.deleteCapManagerCaps(req),
        confirmPostAction: load,
        onCancelAction: null,
      };
      props.openConfirmModal(data);
    } else {
      const data = {
        title: (
          <span>
            {Strings.deleteCapWarning} <b>{cap_id}</b> from organization <b>{selected_device.organization?.name}</b>?
          </span>
        ),
        onConfirmAction: actions.deleteCap(cap_id, selected_device.code),
        confirmPostAction: load,
        onCancelAction: null,
      };
      props.openConfirmModal(data);
    }
  }, [caps, props.openConfirmModal, load]);

  const getHeaderComponents = () => {
    const { onAttachDevice } = props;

    return (
      <>
        <OrgSelect addNoOrg />
        <Button class="refresh" onClick={onRefresh} />
        <TextInput className="search" placeholder={Strings.search} onChange={onTextInputChange} />
        <Select
          data={[
            DateFilters.AllTime,
            DateFilters.Last48Hours,
            DateFilters.ThisMonth,
            DateFilters.Last30Days,
            DateFilters.LastMonth,
            DateFilters.Last6Months,
            DateFilters.ThisYear,
            DateFilters.Last12Months,
          ]}
          onChange={onDateRangeChanged}
          defaultValue={DateFilters.AllTime.value}
        />
        <AdvancedFilters>
          <Filter definition={getFiltersDefinition().Status} />
          <Filter definition={getFiltersDefinition().Sample} />
          <Filter definition={getFiltersDefinition().Signal} />
          <Filter definition={getFiltersDefinition().Battery} />
          <Filter definition={getFiltersDefinition().Faults} />
        </AdvancedFilters>
        <Button class="download" onClick={onDownload} />
        {(isShipper() || isSuperUser()) && (
          <Button
            class={HEADER_BUTTON_DARK_BLUE}
            onClick={() => onAttachDevice(load)}
            title={Strings.attachCap}
          />
        )}
      </>
    );
  };

  const columns = React.useMemo(() => {
    const cols = [
      columnHelper.accessor('cap_id', {
        header: Strings.deviceId,
        cell: info => (
          <React.Fragment>
            <div className="cell-with-select">
              <div className="selector" onClick={e => e.stopPropagation()}>
                <input
                  type="checkbox"
                  className="item-checkbox"
                  id={info.getValue()}
                  onChange={itemChecked}
                  key={keyForItemCheckboxes}
                />
              </div>
              <div className="selector-label">{info.getValue()}</div>
            </div>
          </React.Fragment>
        ),
        size: 200,
        enableSorting: false,
      }),
      columnHelper.accessor('cap_name', {
        header: Strings.deviceName,
        cell: info => info.getValue() || '-',
        enableSorting: false,
      }),
    ];

    if (props.type !== DeviceTypes['Bluetooth Cap'].id) {
      cols.push(
        columnHelper.accessor('imei', {
          header: Strings.imei,
          cell: info => info.getValue() || '-',
          enableSorting: false,
        }),
      );
    }

    cols.push(
      columnHelper.accessor('kit_id', {
        header: Strings.kitId,
        cell: info => info.getValue() || '-',
        enableSorting: false,
      }),
      columnHelper.accessor('is_sample', {
        header: Strings.deviceInvoices.sample,
        cell: info => (info.getValue() ? Strings.yes : Strings.no),
        enableSorting: false,
      }),
    );

    if (props.orgs?.length > 1) {
      cols.push(
        columnHelper.accessor('organization', {
          header: Strings.organizationName,
          cell: info => info.getValue()?.name || '-',
          enableSorting: false,
        }),
      );
    }

    cols.push(
      columnHelper.accessor('last_updated_at', {
        header: Strings.lastActivity,
        cell: info => (info.getValue() ? convertUnixEpochToHumanReadable(info.getValue()) : '-'),
      }),
      columnHelper.accessor('last_activity_type', {
        header: Strings.activityType,
        cell: info => info.getValue() || '-',
        enableSorting: false,
      }),
      columnHelper.accessor('last_vital_at', {
        header: Strings.lastEventTime,
        cell: info => (info.getValue() ? convertUnixEpochToHumanReadable(info.getValue()) : '-'),
        enableSorting: false,
      }),
      columnHelper.accessor('battery', {
        header: Strings.battery.battery,
        cell: info => (
          <BatteryWithChargingStatus
            battery={info.getValue()}
            batteryMeasuredAt={info.row.original.battery_measured_at}
            chargingStatus={info.row.original.charging_status}
          />
        ),
        enableSorting: false,
      }),
      columnHelper.accessor('firmware91', {
        id: 'fota',
        header: Strings.fota,
        cell: info => info.getValue() || '-',
        enableSorting: true,
      }),
      columnHelper.accessor('signal_strength', {
        header: Strings.signalStrength,
        cell: info => signalRender(info.getValue()),
        enableSorting: false,
      }),
      columnHelper.accessor('location', {
        header: Strings.location,
        cell: info => (info.getValue() ? cityDisplay(info.getValue()) : '-'),
        enableSorting: true,
      }),
      columnHelper.accessor('attached', {
        header: Strings.patient,
        cell: info => (info.getValue() ? 'Yes' : 'No'),
        enableSorting: false,
      }),
      columnHelper.accessor('status', {
        header: Strings.status,
        cell: info => (info.getValue() ? ucFirst(info.getValue()) : '-'),
        enableSorting: false,
      }),
    );

    return cols;
  }, [props.type, props.orgs?.length, keyForItemCheckboxes, itemChecked]);

  const actionBtns = (
    <React.Fragment>
      <ActionDropdownOnOffMenu
        img="action-sleepmode"
        tooltiptext={Strings.sleepMode}
        off={{label: Strings.formatString(Strings.actionOptionOff, Strings.sleepMode)}}
        on={{label: Strings.formatString(Strings.actionOptionOn, Strings.sleepMode)}}
        action={value => {
          onSleepModeChange(value);
        }}
      />
      <ActionButton img="action-download" tooltiptext="Download" text="Download" action={() => {}} disabled />
      {!(isOmron() || (isESIOrgName() && isDoctor())) ? (
        <ActionButton
          img="action-delete"
          tooltiptext="Delete"
          text="Delete"
          action={e => {
            onCapsDelete(e, itemSelectorHelper.getFirstItem());
          }}
          disabled={multiselectedItems}
        />
      ) : null}
    </React.Fragment>
  );

  const headerTitle =
    !isLoading && pagination && caps
      ? Strings.formatString(
          Strings.showingXDevices,
          caps.length,
          pagination.totalRecords ? pagination.totalRecords : 0,
        )
      : Strings.showingWait;

  return (
    <div className="tab-page-content">
      <PageHeader
        isBlack
        left={headerTitle}
        right={getHeaderComponents()}
      />
      <AdvancedFiltersBar />
      <Table3
        name="caps"
        data={caps}
        columns={columns}
        onRowClick={onRowSelection}
        onOffsetChange={onOffsetChange}
        onPageSizeChange={onPageSizeChange}
        onSortingChange={onSortClick}
        pagination={pagination}
        isLoading={isLoading}
        enableColumnFiltering
      />
      <ActionWidget show={openActionWidget}>{actionBtns}</ActionWidget>
    </div>
  );
}

LTE.propTypes = {
  downloadEvents: PropTypes.func,
  downloadDetails: PropTypes.func,
  getCaps: PropTypes.func,
  cancelGetCaps: PropTypes.func,
  navigate: PropTypes.func,
  openConfirmModal: PropTypes.func,
  onAttachDevice: PropTypes.func,
  openDateRangeSelectionModal: PropTypes.func,
  showNotification: PropTypes.func,
  featureConfiguration: PropTypes.func,
  filters: PropTypes.array,
  type: PropTypes.string,
  orgIds: PropTypes.string,
  orgs: PropTypes.array,
};

const mapStateToProps = state => ({
  filters: state.entities.advancedFilters.filters.items,
  orgIds: state.entities?.orgFilter?.orgIds,
  orgs: state.entities?.orgFilter?.orgs,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
  getCaps: (pageRequest, signal) => dispatch(actions.getCaps(pageRequest, signal)),
  cancelGetCaps: controller => dispatch(actions.cancelGetCaps(controller)),
  navigate: path => dispatch(push(path)),
  downloadEvents: params => dispatch(actions.downloadReport(params)),
  downloadDetails: params => dispatch(actions.downloadDetails(params)),
  openConfirmModal: data => dispatch(openModalAction('confirmation-modal', data)),
  onAttachDevice: postAction =>
    dispatch(
      openModalAction('attach-cap-to-org', {
        postAction,
        type: ownProps.type,
      }),
    ),
  openDateRangeSelectionModal: (actionOnDone, downloadRequest, orgName) =>
    dispatch(
      openModalAction('date-range-selection-for-events-reports-modal', {
        actionOnDone,
        simplified: true,
        filters: downloadRequest,
        orgName,
      }),
    ),
  showNotification: (message, timeout) => dispatch(notificationActions.show(message, timeout)),
  featureConfiguration: request => dispatch(actions.featureConfiguration(request)),
});

export default connect(mapStateToProps, mapDispatchToProps)(LTE);
